import { useI18n } from '../i18n';
import type { AppErrorRecord } from '../errors/LastAppErrorContext';
import styles from './LastErrorInspectPanel.module.css';

type LastErrorInspectPanelProps = {
  error: AppErrorRecord | null;
  onClose: () => void;
  onClear: () => void;
};

function formatWhen(iso: string, language: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dt);
}

export function LastErrorInspectPanel({ error, onClose, onClear }: LastErrorInspectPanelProps) {
  const { t, language } = useI18n();
  const dir = language === 'he' ? 'rtl' : 'ltr';

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('header.lastErrorTitle')}
      dir={dir}
    >
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>{t('header.lastErrorTitle')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        {!error ? (
          <p className={styles.empty}>{t('header.lastErrorEmpty')}</p>
        ) : (
          <div className={styles.body}>
            <p className={styles.when}>
              <span className={styles.label}>{t('header.lastErrorWhen')}</span>
              {formatWhen(error.at, language)}
            </p>
            <p className={styles.line}>
              <span className={styles.label}>{t('header.lastErrorSource')}</span>
              <code className={styles.code}>{error.source}</code>
            </p>
            {error.request ? (
              <p className={styles.line}>
                <span className={styles.label}>{t('common.request')}</span>
                <code className={styles.code}>{error.request}</code>
              </p>
            ) : null}
            <p className={styles.message} role="alert">
              {error.message}
            </p>
            {error.httpStatus != null ? (
              <p className={styles.line}>
                <span className={styles.label}>HTTP</span>
                <span className={styles.value}>
                  {error.httpStatus}
                  {error.httpStatusText ? ` ${error.httpStatusText}` : ''}
                </span>
              </p>
            ) : null}
            {error.errorCode != null ? (
              <p className={styles.line}>
                <span className={styles.label}>{t('header.lastErrorCode')}</span>
                <span className={styles.value}>{String(error.errorCode)}</span>
              </p>
            ) : null}
            {error.errorDescription ? (
              <p className={styles.line}>
                <span className={styles.label}>{t('header.lastErrorDescription')}</span>
                <span className={styles.value}>{error.errorDescription}</span>
              </p>
            ) : null}
            {error.networkDetails ? (
              <p className={styles.line}>
                <span className={styles.label}>{t('header.lastErrorNetwork')}</span>
                <span className={styles.value}>{error.networkDetails}</span>
              </p>
            ) : null}
            {error.contextJson ? (
              <>
                <p className={styles.preLabel}>{t('header.lastErrorContextJson')}</p>
                <pre className={styles.pre}>{error.contextJson}</pre>
              </>
            ) : null}
            {error.rawBody ? (
              <>
                <p className={styles.preLabel}>{t('header.lastErrorResponseJson')}</p>
                <pre className={styles.pre}>{error.rawBody}</pre>
              </>
            ) : null}
          </div>
        )}

        <div className={styles.actions}>
          {error ? (
            <button type="button" className={styles.clearButton} onClick={onClear}>
              {t('header.lastErrorClear')}
            </button>
          ) : null}
          <button type="button" className={styles.doneButton} onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
